/**
 * RailAddCondition — "Add condition" affordance appended to each group in the
 * Rail view when `editContext` is present (and, as the "+ or" / "+ and" chip,
 * at the end of each Inline group box). Delegates to `VqeFilterChipBuilder`
 * (mode="add") which handles the column picker, DraftFilterChip editing, and
 * predicate lowering; on commit the lowered predicate is inserted at
 * `groupPath` via `addFilterNode`. Analytics fired on commit.
 *
 * Slice 3B.3 — path-addressed WHERE edit.
 * HAVING parity (Plan 3 Task 5): `kind` (default `'where'`) parameterizes the
 * testid and the `feature:vqe_${kind}_node_edit` analytics, threads `clause`
 * into `addFilterNode`, and drives the chip's field-step content (`'having'`
 * leads with the Aggregates templates).
 */

import type { ReactElement } from 'react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { WhereCombinator } from '../../../../sql-resolver/types';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { addFilterNode } from '../../edit/edits/addFilterNode';
import { vqeCopy } from '../../ux/vqeCopy';
import { VqeFilterChipBuilder } from '../common/VqeFilterChipBuilder';

export function RailAddCondition({
  groupPath,
  connector,
  editContext,
  label = vqeCopy.actions.addFilterShort,
  kind = 'where',
  triggerVariant,
  hint,
  onDraftOpenChange,
}: {
  readonly groupPath: readonly number[];
  readonly connector: WhereCombinator;
  readonly editContext: VqeEditContext;
  /**
   * Trigger label — defaults to the shared "Add condition" copy (matching the
   * section-level WHERE/HAVING add button); the Inline group box passes "Add".
   * It must NOT start with a "+": the underlying `CommandPickerPopover` trigger
   * already renders a `<Plus>` icon, so a "+ …" label rendered "+ + condition".
   */
  readonly label?: string;
  readonly kind?: BooleanClauseTarget;
  /** The Inline group box's end chip ("+ or" / "+ and") uses `'chip'`. */
  readonly triggerVariant?: 'ghost' | 'chip';
  readonly hint?: string;
  readonly onDraftOpenChange?: (open: boolean) => void;
}): ReactElement {
  const pathKey = groupPath.join('-') || 'root';
  const { track } = useAnalytics();

  return (
    <VqeFilterChipBuilder
      mode="add"
      testId={`vqe-${kind}-rail-group-${pathKey}-add`}
      triggerLabel={label}
      triggerVariant={triggerVariant}
      hint={hint}
      onDraftOpenChange={onDraftOpenChange}
      editContext={editContext}
      combinator={connector}
      clause={kind}
      onCommit={(text) => {
        void addFilterNode(groupPath, text, connector, toCommitCtx(editContext), kind);
        track(`feature:vqe_${kind}_node_edit`, { action: 'add' });
      }}
    />
  );
}
